import * as React from "react";
import { useState, useEffect } from "react";
import "../CustomStyle.css";
import CustomDataGrid from "../components/CustomDataGrid";
import EntityDetails from "../components/EntityDetails";
import { Spinner, Alert } from "react-bootstrap";
import {
  generateFormData,
  respondedMapping,
  campaignMapping,
  contactVisitsMapping,
  generateGridColumns,
  campaignGridColumns,
  respondedGridColumns,
  contactVisitsGridColumns,
  campaignRowMapping,
  generateGridRows,
  respondedRowMapping,
  contactVisitsRowMapping,
} from "../utils/PageUtils";
import {
  fetchCampaigns,
  fetchRespondedContacts,
  fetchContactVisits,
} from "../utils/api";
import PageHelmet from "../components/PageHelmet";

export default function CampaignsListGrid({ userRole, bearerToken }) {
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const [campaignsData, setCampaignsData] = useState([]);
  const [campaignError, setCampaignError] = useState(null);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [camapaignRowId, setCamapaignRowId] = useState(null);

  const [records, setRecords] = useState([]);
  const [respondError, setRespondError] = useState(null);
  const [respondInfo, setRespondInfo] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactRowId, setContactRowId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [visits, setVisits] = useState([]);
  const [visitError, setVisitError] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitsRowId, setVisitsRowId] = useState(null);
  const [visitsLoading, setVisitsLoading] = useState(false);


  useEffect(() => {
    setCampaignError(null);
    setCampaignInfo(null);
    setIsInitialLoading(true);

    const fetchCampaignsData = async () => {
      if (!bearerToken) {
        return;
      }
      try {
        const response = await fetchCampaigns(bearerToken);

        switch (response.status) {
          case 200: {
            const data = response.data;
            const sortedCampaigns = data.records.toSorted((a, b) => new Date(b.created) - new Date(a.created));
            setCampaignsData(sortedCampaigns);
            break;
          }

          case 400:
            setCampaignError(
              "Something went wrong while attempting to display data on the screen."
            );
            break;

          case 404:
            setCampaignInfo(" No existing campaigns were found for this user.");
            break;

          case 500:
            setCampaignError(
              "Something went wrong while attempting to display data on the screen."
            );
            break;

          default:
            setCampaignError(
              "Something went wrong while attempting to display data on the screen."
            );
        }
      } catch (error) {
        setCampaignError(
          "Something went wrong while attempting to display data on the screen."
        );
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchCampaignsData();
  }, []);

  useEffect(() => {
    if (!selectedCampaign) return;
    const fetchRespondedData = async () => {
      setLoading(true);
      setRespondError(null);
      setRespondInfo(null);
      try {
        const response = await fetchRespondedContacts(
          selectedCampaign.campaign_name,
          bearerToken
        );
        switch (response.status) {
          case 200: {
            const data = await response.data;
            const sortedRecords = data.records.toSorted((a, b) => new Date(b.created) - new Date(a.created));
            setRecords(sortedRecords);
            break;
          }

          case 400:
            setRespondError(
              "Something went wrong while attempting to display data on to the screen"
            );
            break;

          case 404:
            setRespondInfo("No responses to this campaign yet");
            break;

          case 500:
            setRespondError(
              "Something went wrong while attempting to display data on to the screen"
            );
            break;

          default:
            setRespondError(
              "Something went wrong while attempting to display data on to the screen"
            );
        }
      } catch (error) {
        setRespondError(
          "Something went wrong while attempting to display data on to the screen"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchRespondedData();
  }, [selectedCampaign]);

  useEffect(() => {
    if (!selectedContact) return;
    const fetchContactData = async () => {
      setVisitError(null);
      setVisitsLoading(true);
      try {
        const trackable = selectedContact.trackable;
        const response = await fetchContactVisits(trackable, bearerToken);
        switch (response.status) {
          case 200: {
            const data = await response.data;
            const sortedVisits = data.records.toSorted((a, b) => new Date(b.visited) - new Date(a.visited));
            setVisits(sortedVisits);
            break;
          }

          case 400:
            setVisitError(
              "Something went wrong while attempting to display data on to the screen"
            );
            break;

          case 404:
            setVisitInfo("No visits were found for this contact");
            break;

          case 500:
            setVisitError(
              "Something went wrong while attempting to display data on to the screen"
            );
            break;

          default:
            setVisitError(
              "Something went wrong while attempting to display data on to the screen"
            );
        }
      } catch (error) {
        setVisitError(
          "Something went wrong while attempting to display data on to the screen"
        );
      } finally {
        setVisitsLoading(false);
      }
    };

    fetchContactData();
  }, [selectedContact]);

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null;
  const campaignColumns = generateGridColumns(campaignGridColumns, "campaign_name");
  const campaignRows = generateGridRows(campaignsData, campaignRowMapping);

  const respondedFormData = selectedContact
    ? generateFormData(selectedContact, respondedMapping)
    : null;
  const respondedColumns = generateGridColumns(respondedGridColumns, "greeting_name");
  const respondedRows = generateGridRows(records, respondedRowMapping);

  const visitsFormData = selectedVisit
    ? generateFormData(selectedVisit, contactVisitsMapping)
    : null;
  const visitsColumns = generateGridColumns(contactVisitsGridColumns, "visited");
  const visitsRows = generateGridRows(visits, contactVisitsRowMapping);

  function handleCampaignClick(e) {
    setSelectedCampaign(null);
    const selected = campaignsData[e.id - 1];
    setTimeout(() => {
      setSelectedCampaign(selected);
      setSelectedContact(null);
      setSelectedVisit(null);
    }, 0);
    setCamapaignRowId(e.id);
  }

  const handleContactClick = (e) => {
    const selected = records[e.id - 1];
    setSelectedContact(selected);
    setContactRowId(e.id);
  };

  const handleVisitClick = (e) => {
    const selected = visits[e.id - 1];
    setSelectedVisit(selected);
    setVisitsRowId(e.id);
  };
  if (isInitialLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Please wait...</span>
        </Spinner>
      </div>
    );
  }

  return userRole === "customer" ? (
    <div className="campaign-dashboard-container">
      <PageHelmet
        title="Campaign Dashboard"
        description="Track campaigns."
        url={`${process.env.REACT_APP_REDIRECT_URI}/dashboard`}
      />
      <>
        <h1 className="main-heading" data-testid="dashboard-headings">
          Campaign Dashboard
        </h1>

        <div className="create-main-container">
          <div
            className={`create-grid-form ${selectedCampaign ? "grid-to-left" : "grid-centered"
              } `}
          >

            {campaignInfo ? (
              <div className="no-campaigns">
                <Alert variant="primary">{campaignInfo}</Alert>
              </div>
            ) : campaignError ? (
              <div className="no-campaigns">
                <Alert variant="danger">{campaignError}</Alert>
              </div>
            ) : (

              <div className="create-grid-container">
                <h4
                  className="subheading"
                  data-testid="dashboard-headings"
                  style={{ color: "#2f539b" }}
                >
                  Click Campaign for Stats
                </h4>
                <CustomDataGrid
                  rows={campaignRows}
                  columns={campaignColumns}
                  totalRows={campaignsData.length}
                  onRowClick={handleCampaignClick}
                  selectedRowId={camapaignRowId}
                />
              </div>
            )}
            {selectedCampaign && (

              <div className="create-form-container">
                <h4 className="subheading" data-testid="dashboard-headings">
                  Campaign Details
                </h4>
                <EntityDetails inputs={campaignFormData} width={"100px"} />

              </div>
            )}
          </div>
        </div>

      </>

      {selectedCampaign && (
        <div className="create-main-container custom-container">
          {loading && (
            <div className="proof-note">
              <Spinner animation="border" variant="primary" />
              <p>Loading...</p>
            </div>
          )}
          {respondError && (
            <div className="dashboard-msg">
              <Alert variant="danger">{respondError}</Alert>
              {respondError}
            </div>
          )}
          {respondInfo && (
            <div className="dashboard-msg">
              <Alert variant="primary" data-testid="respondInfo">
                {respondInfo}
              </Alert>
            </div>
          )}
          <div className={`create-grid-form ${selectedContact ? "grid-to-left" : "grid-centered"}`}>

            {!loading && !respondError && !respondInfo && (
              <div className="create-grid-container">
                <h4
                  className="subheading"
                  data-testid="dashboard-headings"
                  style={{ color: "#2f539b" }}
                >
                  Responded Contacts - Choose One
                </h4>
                <CustomDataGrid
                  rows={respondedRows}
                  columns={respondedColumns}
                  totalRows={records.length}
                  onRowClick={handleContactClick}
                  selectedRowId={contactRowId}
                />
              </div>
            )}
            {respondedFormData && (

              <div className="create-form-container">
                <h4 className="heading" data-testid="dashboard-headings">
                  Contact Details
                </h4>
                <EntityDetails inputs={respondedFormData} width={"130px"} />
              </div>
            )}

          </div>
        </div>
      )}

      {selectedContact && (
        <div className="create-main-container">
          <div
            className={`create-grid-form ${selectedVisit ? "grid-to-left" : "grid-centered"
              } `}
          >
            {visitsLoading && (
              <div className="proof-note">
                <Spinner animation="border" variant="primary" />
                <p>Loading...</p>
              </div>
            )}
            {visitError && (
              <div className="dashboard-msg">
                <Alert variant="danger">{visitError}</Alert>
              </div>
            )}

            {!visitsLoading && !visitError && (
              <div className="create-grid-container">
                <h4
                  className="subheading"
                  data-testid="dashboard-headings"
                  style={{ color: "#2f539b" }}
                >
                  Contact Visits
                </h4>
                <CustomDataGrid
                  rows={visitsRows}
                  columns={visitsColumns}
                  totalRows={visits.length}
                  onRowClick={handleVisitClick}
                  selectedRowId={visitsRowId}
                />
              </div>
            )}
            {visitsFormData && (

              <div id="form-testId" className="create-form-container">
                <h4 className="heading" data-testid="dashboard-headings">
                  Visit Details
                </h4>
                <EntityDetails inputs={visitsFormData} width={"100px"} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
}
